/**
 * 1: Setting no validate or validationSchema clears out imperative error state on onBlur/onChange
 * https://github.com/jaredpalmer/formik/issues/834
 *
 * We abuse 'status' in this case
 */
import React, { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useFormikContext } from 'formik';
import { Button, Col, Form } from 'react-bootstrap';
import { SelectField } from '@components/Form/SelectField';
import { ActivityIndicator } from '@components/ActivityIndicator/ActivityIndicator';
import { DEFAULT_COUNTRY_CODE } from '@constants/constants';
import { BusinessInformation } from './BusinessInformation';
import { BusinessInformationExtra } from './BusinessInformationExtra';
import { LoginData } from './LoginData';

interface IRegisterBusinessAccountProps {
  countryData: ICountry[];
}

export const RegisterBusinessAccount = ({ countryData, ...props }: IRegisterBusinessAccountProps) => {
  const { t } = useTranslation('register');
  const { setFieldValue, handleSubmit, isSubmitting, status, errors, setErrors } = useFormikContext();

  useEffect(() => {
    const defaultCountry = countryData.find((country) => country.code === DEFAULT_COUNTRY_CODE);
    setFieldValue('countryCode', JSON.stringify(defaultCountry));
  }, [countryData, setFieldValue]);

  const scrollToErrors = () => {
    setTimeout(() => {
      const element = document.querySelector('.form-control.is-invalid');
      // element.scrollInToView is very inconsistent
      element &&
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
    }, 0);
  };

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    // [1]
    const formErrors = Object.assign({
      ...status,
      ...errors
    });

    // Nasty check to see if object is empty
    if (JSON.stringify(formErrors) !== '{}') {
      setErrors(formErrors);
    } else {
      handleSubmit();
    }
  };

  const defaultCountry = countryData.find((country) => country.code === DEFAULT_COUNTRY_CODE);

  return (
    <>
      {isSubmitting && <ActivityIndicator blockUI />}
      <Form noValidate onSubmit={handleFormSubmit}>
        <div className="border border-bottom-0">
          <Form.Row>
            <Form.Group as={Col} xs={12} md={6}>
              <SelectField label={t('label.country')} name="countryCode" defaultValue={JSON.stringify(defaultCountry)}>
                <option value=""></option>
                {countryData.map((country) => (
                  <option key={country.code} value={JSON.stringify(country)}>
                    {country.name}
                  </option>
                ))}
              </SelectField>
            </Form.Group>
          </Form.Row>
        </div>
        <div className="border border-bottom-0">
          <BusinessInformation countryData={countryData} />
        </div>
        <div className="border border-bottom-0">
          <BusinessInformationExtra {...props} />
        </div>
        <div className="border">
          <LoginData />
        </div>
        <div className="mt-3 text-right">
          <Button
            as="input"
            type="submit"
            size={'xl' as any}
            disabled={isSubmitting}
            value={t('button.create-account')}
            data-testid="register-submit"
            onClick={() => scrollToErrors()}
          />
        </div>
      </Form>
    </>
  );
};

export default RegisterBusinessAccount;
